<template>
    <div id="print-queue-container">
        <card title="Active Prescription Queue">
            <template #header-right>
                <div class="d-flex justify-content-end align-items-center toolbar">
                    <b-button variant="info" size="sm"
                        @click="$bvModal.show('active-queue-column-customize-modal')">
                        <i class="fa fa-cog"></i> Customize Columns
                    </b-button>
                </div>
            </template>

            <QueueVisibilityModal id="active-queue-column-customize-modal" title="Customize Table Columns"
                description="Toggle which columns appear in the Active Queue table."
                v-model="columnVisibility" :items="columnItems" :defaults="defaultColumnVisibility"
                @save="saveColumnPreferences" />

            <div class="filter-area mb-3">
                <QueueFilterChips :filters="filterSchema" :pinned-keys="pinnedFilterKeys"
                    :revealed-keys="revealedFilterKeys" @filter-revealed="onFilterRevealed"
                    @filter-hidden="onFilterHidden">
                    <template #header-extra="{ filter }">
                        <div v-if="filter.key === 'statuses'" class="d-flex align-items-center">
                            <b-check v-model="showAllStatuses" switch :disabled="isLoading"
                                class="show-all-statuses-toggle">
                                <span style="margin-top: 2px;display:inline-block;">Show all</span>
                            </b-check>
                            <HelpPopup>
                                <p>By default the queue excludes Transfer Out, Expired, Void, Discontinued, and the Rx Denied substatus.</p>
                                <p>Enable to include them in this filter.</p>
                            </HelpPopup>
                        </div>
                    </template>

                    <template #editor="{ filter }">
                        <SearchComponent v-if="filter.key === 'store'" v-model="storeFilter"
                            search-u-r-l="/Store" :params="{ active: true }" :prepopulate="true"
                            :disabled="isLoading" :clearable="true" :hide-label="true"
                            :option-label="storeOptionLabel" />

                        <SearchComponent v-else-if="filter.key === 'program'" v-model="programFilter"
                            search-u-r-l="/Program" :params="{ active: true }" :prepopulate="true"
                            :disabled="isLoading" :clearable="true" :hide-label="true"
                            :option-label="programOptionLabel" />

                        <multiselect v-else-if="filter.key === 'statuses'" ref="statusesMultiselect"
                            v-model="statuses" :options="filteredStatusCodeOptions" multiple
                            :close-on-select="false" :clear-on-select="false" :preserve-search="true"
                            label="name" track-by="value" :limit="2" :disabled="isLoading" placeholder=""
                            @select="onMultiselectSelect('statusesMultiselect')"
                            @close="onMultiselectClose('statusesMultiselect')" />

                        <multiselect v-else-if="filter.key === 'substatuses'" ref="substatusesMultiselect"
                            v-model="substatuses" :options="filteredSubStatusCodeOptions" multiple
                            :close-on-select="false" :clear-on-select="false" :preserve-search="true"
                            label="name" track-by="value" :limit="2" :disabled="isLoading" placeholder=""
                            @select="onMultiselectSelect('substatusesMultiselect')"
                            @close="onMultiselectClose('substatusesMultiselect')" />

                        <div v-else-if="filter.key === 'claims'" class="d-flex align-items-center">
                            <b-check v-model="requireActiveClaim" switch :disabled="isLoading">
                                <span style="margin-top: 2px;display:inline-block;">Only with active claim</span>
                            </b-check>
                            <HelpPopup>
                                <p>Restricts the queue to prescriptions with at least one unreversed claim (online or offline) attached.</p>
                                <p>The "no order created" filter still applies.</p>
                            </HelpPopup>
                        </div>

                        <div v-else-if="filter.key === 'fillDate'" class="fill-date-stack">
                            <el-date-picker size="small" v-model="startDate" today-button reset-button
                                :disabled="isLoading" placeholder="From" />
                            <span class="fill-date-separator">to</span>
                            <el-date-picker size="small" v-model="endDate" today-button reset-button
                                :disabled="isLoading" placeholder="To" />
                        </div>
                    </template>
                </QueueFilterChips>
            </div>

            <PrescriptionsListComponent :defaultSort="sortBy" :prescriptions="prescriptions" showPatientName
                alwaysShowVoid defaultDesc :showSelectCheckox="false" :isLoading="isLoading" :actionButtons="['edit']"
                :additional-fields="additionalFields" :fields-keys-to-remove="hiddenStandardColumnKeys"
                color-code-prescriptions
                :manualSort="['productNameShort', 'patientName', 'fillDate', 'storeName','rxId', 'dispensed',
                'programName', 'patientPay']"
                @sort-changed="handleSort" />

            <BottomPagerBar v-model="pageNumber" :total-rows="totalRows" :per-page="pageSize" show-total
                show-page-size-selector item-name="prescription" @change-page-size="pageSize = $event"
                :loading="isLoading" />

        </card>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { DatePicker } from "element-ui";
import SearchComponent from '@/components/SearchComponent.vue';
import PrescriptionsListComponent from '@/components/Prescription/PrescriptionsListComponent.vue';
import { Prescription, RefillStatusCode } from '@/models/Prescription';
import { Store } from '@/models/Store';
import HelpPopup from "@/components/HelpPopup.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import Multiselect from 'vue-multiselect';
import moment from 'moment';
import { Program } from "@/models/Program";
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import PhoneFormatter from '@/util/PhoneFormatter';
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';
import { saveQueueFilters, loadQueueFilters } from '@/util/queueFilterPersistence';
import TextUtils from '@/util/TextUtils';
import { getUserSettingParsed, saveUserSetting } from '@/services/UserSettingsService';
import QueueVisibilityModal, { VisibilityItem } from '@/components/Queue/QueueVisibilityModal.vue';
import QueueFilterChips, { FilterDef } from '@/components/Queue/QueueFilterChips.vue';

const COLUMN_PREF_KEY = 'activeQueueColumnPreferences';
const REVEALED_FILTERS_PREF_KEY = 'activeQueueRevealedFilters';

@Component({
    name: "ActiveQueue",
    components: {
        SearchComponent,
        [DatePicker.name]: DatePicker,
        PrescriptionsListComponent,
        HelpPopup,
        Multiselect,
        BottomPagerBar,
        QueueVisibilityModal,
        QueueFilterChips,
    },
})
export default class ActiveQueue extends Vue {
    protected prescriptions: Array<Prescription> = [];
    private storeFilter: Store = new Store();
    private programFilter: Program = new Program();
    protected startDate?: string = moment().subtract(14, 'days').toISOString();
    protected endDate?: string = moment().toISOString();

    protected isLoading: boolean = false;

    protected totalRows: number = 0;
    protected pageNumber: number = 1;
    protected pageSize: number = 50;
    protected statuses: Array<any> = [];
    protected substatuses: Array<any> = [];
    protected showAllStatuses: boolean = false;
    protected requireActiveClaim: boolean = false;

    private sortBy: string = "fillDate";
    private sortDirection: string = "D";

    // ---- Column visibility ----
    private readonly defaultColumnVisibility: { [key: string]: boolean } = {
        productNameShort: true, patientName: true, patientPhone: true, patientCell: true,
        addressState: true, storeName: true, rxId: true, daySupply: true, dispensed: true,
        qtyLeft: true, fillDate: true, patientPay: true, programName: true, lastShippedDate: true,
        primaryPlanName: false, primaryClaimStatus: false,
        secondaryPlanName: false, secondaryClaimStatus: false,
        tertiaryPlanName: false, tertiaryClaimStatus: false,
    };

    protected columnVisibility: { [key: string]: boolean } = { ...this.defaultColumnVisibility };

    private readonly columnItems: VisibilityItem[] = [
        { key: 'productNameShort', label: 'Drug', group: 'Standard Columns' },
        { key: 'patientName', label: 'Patient', group: 'Standard Columns' },
        { key: 'patientPhone', label: 'Patient Phone', group: 'Standard Columns' },
        { key: 'patientCell', label: 'Patient Cell', group: 'Standard Columns' },
        { key: 'addressState', label: 'Patient State', group: 'Standard Columns' },
        { key: 'storeName', label: 'Store', group: 'Standard Columns' },
        { key: 'rxId', label: 'Rx Id', group: 'Standard Columns' },
        { key: 'daySupply', label: 'Day Supply', group: 'Standard Columns' },
        { key: 'dispensed', label: 'Dispensed', group: 'Standard Columns' },
        { key: 'qtyLeft', label: 'Qty Left', group: 'Standard Columns' },
        { key: 'fillDate', label: 'Fill Date', group: 'Standard Columns' },
        { key: 'patientPay', label: 'Patient Pay', group: 'Standard Columns' },
        { key: 'programName', label: 'Program', group: 'Standard Columns' },
        { key: 'lastShippedDate', label: 'Last Shipped', group: 'Standard Columns' },
        { key: 'primaryPlanName', label: 'Primary Plan', group: 'Claim Columns' },
        { key: 'primaryClaimStatus', label: 'Primary Status', group: 'Claim Columns' },
        { key: 'secondaryPlanName', label: 'Secondary Plan', group: 'Claim Columns' },
        { key: 'secondaryClaimStatus', label: 'Secondary Status', group: 'Claim Columns' },
        { key: 'tertiaryPlanName', label: 'Tertiary Plan', group: 'Claim Columns' },
        { key: 'tertiaryClaimStatus', label: 'Tertiary Status', group: 'Claim Columns' },
    ];

    private readonly standardColumnKeys = [
        'productNameShort', 'patientName', 'storeName', 'rxId',
        'daySupply', 'dispensed', 'qtyLeft', 'fillDate', 'patientPay', 'programName',
    ];

    // Full field order PrescriptionsListComponent renders before any splices/removals.
    // Used to compute a dynamic insert index for the contact columns so they still
    // land next to "Patient" even when an earlier column is hidden.
    private readonly baseColumnOrder = [
        'select', 'hasAddons', 'productNameShort', 'patientName', 'storeName',
        'rxId', 'daySupply', 'dispensed', 'qtyLeft', 'fillDate', 'patientPay', 'programName',
    ];

    get hiddenStandardColumnKeys() {
        return this.standardColumnKeys.filter(k => !this.columnVisibility[k]);
    }

    private patientNameInsertIndex(): number | null {
        const removed = new Set(this.hiddenStandardColumnKeys);
        const visible = this.baseColumnOrder.filter(k => !removed.has(k));
        const idx = visible.indexOf('patientName');
        return idx >= 0 ? idx : null;
    }

    get additionalFields() {
        // PrescriptionsListComponent filters fields-keys-to-remove BEFORE splicing
        // additionalFields, so we compute the contact columns' insert index against
        // the post-removal order. If Patient is hidden, fall back to appending.
        const patientIdx = this.patientNameInsertIndex();
        const phoneIdx = patientIdx;
        const cellIdx = patientIdx != null ? patientIdx + 1 : null;
        const stateIdx = patientIdx != null ? patientIdx + 2 : null;

        const optionalFields: Array<{ index: number | null; field: any; visibilityKey: string }> = [
            { index: phoneIdx, visibilityKey: 'patientPhone', field: { key: 'patientPhone', formatter: this.phoneFormatter } },
            { index: cellIdx, visibilityKey: 'patientCell', field: { key: 'patientCell', formatter: this.phoneFormatter } },
            { index: stateIdx, visibilityKey: 'addressState', field: { key: 'addressState', label: 'Patient State' } },
            { index: null, visibilityKey: 'primaryPlanName', field: { key: 'primaryPlanName', label: 'Primary Plan', sortable: false } },
            { index: null, visibilityKey: 'primaryClaimStatus', field: { key: 'primaryClaimStatus', label: 'Primary Status', sortable: false } },
            { index: null, visibilityKey: 'secondaryPlanName', field: { key: 'secondaryPlanName', label: 'Secondary Plan', sortable: false } },
            { index: null, visibilityKey: 'secondaryClaimStatus', field: { key: 'secondaryClaimStatus', label: 'Secondary Status', sortable: false } },
            { index: null, visibilityKey: 'tertiaryPlanName', field: { key: 'tertiaryPlanName', label: 'Tertiary Plan', sortable: false } },
            { index: null, visibilityKey: 'tertiaryClaimStatus', field: { key: 'tertiaryClaimStatus', label: 'Tertiary Status', sortable: false } },
            { index: null, visibilityKey: 'lastShippedDate', field: { key: 'lastShippedDate', label: 'Last Shipped', sortable: false, formatter: this.lastShippedFormatter } },
        ];

        return optionalFields
            .filter(f => this.columnVisibility[f.visibilityKey])
            .map(({ index, field }) => ({ index, field }));
    }

    // Filters shown by default in the bar. Others (e.g. "Has active claim") live under
    // the "+ Filter" dropdown; once added they are remembered in revealedFilterKeys.
    private readonly defaultFilterKeys = ['store', 'program', 'statuses', 'substatuses', 'fillDate'];

    get pinnedFilterKeys(): string[] {
        return this.defaultFilterKeys;
    }

    // Non-default filters shown in the bar beyond the always-on defaults. Seeded with
    // "claims" so "Has active claim" is pinned by default, but it stays removable via its
    // "x" (which returns it to "+ Filter"). Persisted to user prefs, so once a user adds
    // or removes a filter their choice sticks across sessions and overrides this seed.
    protected revealedFilterKeys: string[] = ['claims'];

    onFilterRevealed(key: string) {
        if (!this.revealedFilterKeys.includes(key)) {
            this.revealedFilterKeys = [...this.revealedFilterKeys, key];
            this.saveRevealedFilters();
        }
    }

    onFilterHidden(key: string) {
        if (this.revealedFilterKeys.includes(key)) {
            this.revealedFilterKeys = this.revealedFilterKeys.filter(k => k !== key);
            this.saveRevealedFilters();
        }
    }

    private loadRevealedFilters() {
        const parsed = getUserSettingParsed<string[] | null>(REVEALED_FILTERS_PREF_KEY, null);
        if (Array.isArray(parsed)) this.revealedFilterKeys = parsed;
    }

    private saveRevealedFilters() {
        saveUserSetting(REVEALED_FILTERS_PREF_KEY, JSON.stringify(this.revealedFilterKeys));
    }

    // ---- Filter schema (consumed by all three layout modes) ----
    get filterSchema(): FilterDef[] {
        return [
            {
                key: 'store',
                label: 'Store',
                isActive: () => !!this.storeFilter?.id,
                summary: () => this.storeFilter?.name || '',
                clear: () => { this.storeFilter = new Store(); },
            },
            {
                key: 'program',
                label: 'Program',
                isActive: () => !!this.programFilter?.id,
                summary: () => this.programFilter?.name || '',
                clear: () => { this.programFilter = new Program(); },
            },
            {
                key: 'statuses',
                label: 'Statuses',
                isActive: () => this.statuses.length > 0,
                summary: () => this.statuses.map(s => s.name).join(', '),
                clear: () => { this.statuses = []; },
                valueWidth: 160,
            },
            {
                key: 'substatuses',
                label: 'Substatuses',
                isActive: () => this.substatuses.length > 0,
                summary: () => this.substatuses.map(s => s.name).join(', '),
                clear: () => { this.substatuses = []; },
                valueWidth: 160,
            },
            {
                key: 'claims',
                label: 'Has active claim',
                isActive: () => this.requireActiveClaim,
                summary: () => 'On',
                clear: () => { this.requireActiveClaim = false; },
                toggle: () => { this.requireActiveClaim = !this.requireActiveClaim; },
            },
            {
                key: 'fillDate',
                label: 'Fill Date',
                isActive: () => !!this.startDate || !!this.endDate,
                summary: () => this.formatDateRange(this.startDate, this.endDate),
                clear: () => { this.startDate = ''; this.endDate = ''; },
            },
        ];
    }

    private formatDateRange(start?: string, end?: string): string {
        const fmt = (s?: string) => s ? moment(s).format('M/D/YY') : '…';
        if (!start && !end) return '';
        return `${fmt(start)} – ${fmt(end)}`;
    }

    phoneFormatter(value: string, key: string, item: any) {
        return PhoneFormatter.formatForDisplay(value);
    }

    storeOptionLabel = (s: any): string => s?.name || s?.storeNumber || '';
    programOptionLabel = (p: any): string => p?.name || '';

    lastShippedFormatter(value: Date | string | null, _key: string, item: any): string {
        return TextUtils.lastShippedFormatter(value, item?.lastShippedIsEquivalent);
    }
    get startRowIndex() {
        return this.pageNumber * this.pageSize - this.pageSize;
    }

    get endRowIndex() {
        return this.startRowIndex + this.prescriptions.length;
    }

    get statusCodeOptions() {
        return Object.keys(RefillStatusCode).filter(key => isNaN(Number(key)))
            .map(key => ({
                name: key,
                value: (RefillStatusCode as any)[key]
            }))
    }

    private excludedStatuses = [
        RefillStatusCode.TransferOut,
        RefillStatusCode.Expired,
        RefillStatusCode.Void,
        RefillStatusCode.Discontinued
    ];

    get filteredStatusCodeOptions() {
        if (this.showAllStatuses) {
            return this.statusCodeOptions;
        }
        return this.statusCodeOptions.filter(s => !this.excludedStatuses.includes(s.value));
    }

    private subStatusCodeOptions: Array<any> = [];
    private excludedSubstatusId = 14;

    get filteredSubStatusCodeOptions() {
        if (this.showAllStatuses) {
            return this.subStatusCodeOptions;
        }
        return this.subStatusCodeOptions.filter((ss: any) => ss.value !== this.excludedSubstatusId);
    }

    // vue-multiselect closes its dropdown on the post-select focus shake even when
    // close-on-select is false. Track the just-selected ref name and re-activate it
    // on the next tick so the user can keep picking without reopening manually.
    private justSelectedMultiselect: string | null = null;
    private readonly multiselectRefNames = ['statusesMultiselect', 'substatusesMultiselect'];

    onMultiselectSelect(refName: string) {
        this.justSelectedMultiselect = refName;
    }

    onMultiselectClose(refName: string) {
        if (this.justSelectedMultiselect !== refName) return;
        this.justSelectedMultiselect = null;
        this.$nextTick(() => {
            const ms = this.$refs[refName] as any;
            if (ms && typeof ms.activate === 'function') ms.activate();
        });
    }

    // When the chip's multiselect lives inside a b-popover, vue-multiselect's
    // blur-based auto-close doesn't reliably fire on outside clicks. Detect them
    // explicitly and call deactivate() so clicking anywhere outside the field
    // closes the dropdown.
    private onDocMousedownForMultiselect = (e: MouseEvent) => {
        const target = e.target as HTMLElement | null;
        if (!target) return;
        for (const refName of this.multiselectRefNames) {
            const ms = this.$refs[refName] as any;
            if (!ms || !ms.isOpen) continue;
            const rootEl: HTMLElement | undefined = ms.$el;
            if (rootEl && rootEl.contains(target)) continue;
            // Clear the "just selected" flag too so onMultiselectClose doesn't reopen.
            if (this.justSelectedMultiselect === refName) this.justSelectedMultiselect = null;
            if (typeof ms.deactivate === 'function') ms.deactivate();
        }
    };

    async getSubStatusCodeOptions() {
        try {
            const resp = await this.$http.get("/api/Configuration");
            this.subStatusCodeOptions = resp.data.onHoldSubStatuses.map((ss: any) => ({
                name: ss.name,
                value: ss.id
            }));
        } catch {
            // ignore
        }
    }

    private skipFilterSave = false;

    mounted() {
        this.pageSize = getPageSizePreference(this, 'pageSize_ActiveQueue', 50);
        this.restoreFilters();
        this.loadColumnPreferences();
        this.loadRevealedFilters();
        this.getSubStatusCodeOptions();
        this.load();
        document.addEventListener('mousedown', this.onDocMousedownForMultiselect, true);
    }

    // userSettings hydrate async over SignalR after mounted(); re-apply prefs the
    // first time the keyed values actually land so a fresh session with no
    // localStorage doesn't silently fall back to defaults.
    @Watch('$root.$data.userSettings', { immediate: true, deep: true })
    onUserSettingsHydrated() {
        this.loadColumnPreferences();
        this.loadRevealedFilters();
    }

    beforeDestroy() {
        document.removeEventListener('mousedown', this.onDocMousedownForMultiselect, true);
        if (this.loadDebounceHandle !== null) {
            window.clearTimeout(this.loadDebounceHandle);
            this.loadDebounceHandle = null;
        }
    }

    private loadColumnPreferences() {
        const parsed = getUserSettingParsed<{ [key: string]: boolean } | null>(COLUMN_PREF_KEY, null);
        if (parsed) this.columnVisibility = { ...this.defaultColumnVisibility, ...parsed };
    }

    saveColumnPreferences() {
        saveUserSetting(COLUMN_PREF_KEY, JSON.stringify(this.columnVisibility));
    }

    @Watch("pageSize")
    onPageSizeChanged() {
        savePageSizePreference(this, 'pageSize_ActiveQueue', this.pageSize);
    }

    private restoreFilters() {
        const saved = loadQueueFilters(this, 'activeQueue_filters');
        if (!saved) return;
        this.skipFilterSave = true;
        if (saved.store?.id) this.storeFilter = new Store(saved.store);
        if (saved.program?.id) this.programFilter = new Program(saved.program);
        if (typeof saved.showAllStatuses === 'boolean') this.showAllStatuses = saved.showAllStatuses;
        if (typeof saved.requireActiveClaim === 'boolean') this.requireActiveClaim = saved.requireActiveClaim;
        this.$nextTick(() => { this.skipFilterSave = false; });
    }

    private persistFilters() {
        if (this.skipFilterSave) return;
        saveQueueFilters(this, 'activeQueue_filters', {
            store: this.storeFilter?.id ? { id: this.storeFilter.id, name: this.storeFilter.name } : null,
            program: this.programFilter?.id ? { id: this.programFilter.id, name: this.programFilter.name } : null,
            showAllStatuses: this.showAllStatuses,
            requireActiveClaim: this.requireActiveClaim,
        });
    }

    @Watch("endDate")
    handleStartDate() {
        if (this.endDate && !this.startDate)
            this.startDate = this.endDate;
        if (!this.endDate)
            this.startDate = "";
    }

    private loadDebounceHandle: number | null = null;

    private requestLoad() {
        if (this.loadDebounceHandle !== null) window.clearTimeout(this.loadDebounceHandle);
        this.loadDebounceHandle = window.setTimeout(() => {
            this.loadDebounceHandle = null;
            this.load();
        }, 300);
    }

    @Watch("statuses")
    @Watch("substatuses")
    @Watch("storeFilter")
    @Watch("startDate")
    @Watch("endDate")
    @Watch("programFilter")
    @Watch("requireActiveClaim")
    resetPageNumber() {
        if (this.skipFilterSave) return;
        this.persistFilters();
        this.pageNumber = 1;
        this.requestLoad();
    }

    @Watch("showAllStatuses")
    handleShowAllStatusesChange() {
        // Clearing statuses + substatuses + pageNumber fires their own @Watch handlers
        // (resetPageNumber / onPageNumberChange) which both route through requestLoad —
        // calling this.load() here would race a second fetch ~300ms later with identical
        // params. Reassigning the arrays unconditionally is intentional: vue 2 reacts on
        // reference change so an already-empty array still notifies, which we want.
        this.statuses = [];
        this.substatuses = [];
        this.persistFilters();
        this.pageNumber = 1;
    }

    @Watch("pageNumber")
    onPageNumberChange() {
        this.requestLoad();
    }

    load() {
        this.isLoading = true;

        const params = {
            pageSize: this.pageSize,
            pageNumber: this.pageNumber,
            storeId: this.storeFilter?.id,
            statuses: this.statuses.map(s => s.value).join(","),
            substatuses: this.substatuses.map(s => s.value).join(","),
            startDate: this.startDate,
            endDate: this.endDate,
            programId: this.programFilter?.id,
            sortBy: this.sortBy,
            sortDirection: this.sortDirection,
            showAllStatuses: this.showAllStatuses,
            requireActiveClaim: this.requireActiveClaim,
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });

        this.$http.get<any>('api/Prescription/active-queue', { params: params })
            .then(response => {
                this.prescriptions = response.data.scripts;
                this.totalRows = response.data.totalRows;
            })
            .catch(error => {
                console.error("Error while loading active queue.", { error, response: error?.response });
                this.$notification(NotificationOptions.error(error));
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    handleSort(event: any) {
        this.sortBy = event.sortBy;
        this.sortDirection = event.sortDesc ? "D" : "A";
        this.pageNumber = 1;
        this.load();
    }
}

</script>

<style scoped>
.fill-date-stack {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.fill-date-stack :deep(.el-date-editor),
.fill-date-stack :deep(.el-input) {
    width: 100%;
}

.fill-date-separator {
    text-align: center;
    font-size: 0.75rem;
    color: #868e96;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.show-all-statuses-toggle {
    font-size: 0.75rem;
    color: #6c757d;
    margin-bottom: 0;
    white-space: nowrap;
}

.show-all-statuses-toggle :deep(.custom-control-label) {
    cursor: pointer;
}
</style>
